import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { assertGenerationAccess } from "./access.server";
import { z } from "zod";

export const TUTOR_SYSTEM_PROMPT = `You are "Build Your Own" — the SmartEssay.AI Tutor inside SmartEssay.ai. Your ONLY job is to make the student think and write their own essay. You are strictly NOT allowed to write full paragraphs, full essays, or complete sentences for the student.

Rules you MUST follow at all times:
- Respond with guiding questions or short suggestions only. Keep every reply to 1-2 sentences unless the student asks for a structured outline.
- When the student gives an idea, ask follow-up questions that push them to clarify their thinking, find evidence, or improve their reasoning.
- Never write the student's thesis, topic sentences, evidence sentences, or conclusions for them. You may suggest the SHAPE of a sentence (e.g. "try starting with a claim that names WHO and WHAT") but never the actual sentence.
- If the student asks you to "just write it" or tries to get a full draft, gently redirect: "I won't write it for you, but I'll help you make yours even stronger. What's your idea for the first main point?"
- If the student pastes their source/title, use it to ask sharper, specific questions about the work — but still never write their essay for them.
- Tone: encouraging, patient, and supportive — like a great teacher who believes the student can do this and is excited to watch them grow.
- Use the student's name/ideas back to them. Celebrate small wins.`;

type CheckFeedback = {
  rating: "great" | "almost" | "needs_work";
  praise: string;
  suggestions: string[];
  encouragement: string;
};

function goodEnoughRule(attempt: number, kind: "thesis" | "paragraph") {
  if (attempt < 4) return "";
  return `\n\nIMPORTANT GOOD-ENOUGH RULE: This is attempt ${attempt}. Do NOT keep moving the goalposts. If the student's ${kind} does the main job and is about 75% strong, even if it is not perfect, mark it as "great", give zero suggestions, and tell them this is strong enough to move on. Only keep "needs_work" if a core required piece is missing or the writing is truly unclear.`;
}

function countWordsForFeedback(text: string) {
  return text.trim().split(/\s+/).filter(Boolean).length;
}

function normalizeForComparison(text: string) {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function wordOverlapRatio(a: string, b: string) {
  const aWords = new Set(normalizeForComparison(a).split(" ").filter(Boolean));
  const bWords = new Set(normalizeForComparison(b).split(" ").filter(Boolean));
  if (!aWords.size || !bWords.size) return 0;
  let overlap = 0;
  for (const word of aWords) if (bWords.has(word)) overlap += 1;
  return overlap / Math.min(aWords.size, bWords.size);
}

function isVeryCloseToExample(studentText: string, exampleText?: string) {
  if (!exampleText?.trim()) return false;
  const student = normalizeForComparison(studentText);
  const example = normalizeForComparison(exampleText);
  if (!student || !example) return false;
  return (
    student === example ||
    student.includes(example) ||
    example.includes(student) ||
    wordOverlapRatio(student, example) >= 0.78
  );
}

function normalizeGoodEnoughFeedback<T extends CheckFeedback>(
  feedback: T,
  attempt: number,
  hasEnoughEffort = true,
  forceAccept = false,
): T {
  if (!forceAccept && (attempt < 4 || !hasEnoughEffort || feedback.rating === "needs_work")) {
    return feedback;
  }
  return {
    ...feedback,
    rating: "great",
    suggestions: [],
    encouragement:
      "You've given this a strong effort. This is good enough to move on and keep building the essay.",
  } as T;
}

const MessageSchema = z.object({
  role: z.enum(["user", "assistant"]),
  content: z.string().min(1).max(8000),
});

const ChatInput = z.object({
  source_title: z.string().max(300).optional(),
  source_text: z.string().max(40000).optional(),
  messages: z.array(MessageSchema).min(1).max(200),
});

export const tutorReply = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => ChatInput.parse(input))
  .handler(async ({ data, context }) => {
    await assertGenerationAccess(context.supabase, context.userId);

    const { generateText } = await import("ai");
    const { createLovableAiGatewayProvider } = await import("./ai-gateway.server");

    const key = process.env.LOVABLE_API_KEY;
    if (!key) throw new Error("Missing LOVABLE_API_KEY");

    const gateway = createLovableAiGatewayProvider(key);
    const model = gateway("google/gemini-2.5-flash");

    const sourceContext =
      data.source_title || data.source_text
        ? `\n\nSTUDENT'S SOURCE:\n${data.source_title ? `Title: ${data.source_title}\n` : ""}${
            data.source_text ? `Source text:\n"""\n${data.source_text}\n"""` : ""
          }`
        : "";

    const { text } = await generateText({
      model,
      system: TUTOR_SYSTEM_PROMPT + sourceContext,
      messages: data.messages.map((m) => ({ role: m.role, content: m.content })),
    });

    const trimmed = text.trim();
    if (!trimmed) throw new Error("Empty reply from tutor.");
    return { reply: trimmed };
  });

const EndInput = z.object({
  source_title: z.string().max(300).optional(),
  source_text: z.string().max(40000).optional(),
  messages: z.array(MessageSchema).min(2).max(200),
});

export const endTutorSession = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => EndInput.parse(input))
  .handler(async ({ data, context }) => {
    await assertGenerationAccess(context.supabase, context.userId);

    const { generateText } = await import("ai");
    const { createLovableAiGatewayProvider } = await import("./ai-gateway.server");

    const key = process.env.LOVABLE_API_KEY;
    if (!key) throw new Error("Missing LOVABLE_API_KEY");

    const gateway = createLovableAiGatewayProvider(key);
    const model = gateway("google/gemini-2.5-flash");

    const transcript = data.messages
      .map((m) => `${m.role === "user" ? "STUDENT" : "TUTOR"}: ${m.content}`)
      .join("\n\n");

    const { text } = await generateText({
      model,
      system: `You evaluate a SmartEssay.AI Tutor conversation. Estimate what percentage of the actual THINKING AND WRITING in this session was done by the STUDENT vs the TUTOR. The tutor should only ask questions and give tiny nudges — if the tutor ever wrote sentences or ideas for the student, that lowers the student's percentage. Be honest but encouraging. Output STRICT JSON only, no markdown fences:
{"brain_work_score": <integer 0-100>, "positive_note": "<one short, warm sentence about what the student learned or did well>"}`,
      prompt: `Evaluate this session:\n\n${transcript}`,
    });

    let parsed: { brain_work_score: number; positive_note: string };
    try {
      const clean = text
        .trim()
        .replace(/^```(?:json)?/i, "")
        .replace(/```$/, "")
        .trim();
      parsed = JSON.parse(clean);
    } catch {
      parsed = {
        brain_work_score: 90,
        positive_note: "You stuck with the questions and pushed your own thinking forward.",
      };
    }
    const score = Math.max(0, Math.min(100, Math.round(parsed.brain_work_score ?? 90)));

    const title = (
      data.source_title?.trim() ||
      data.messages.find((m) => m.role === "user")?.content.slice(0, 80) ||
      "Tutoring session"
    ).trim();

    const output = {
      kind: "tutor" as const,
      brain_work_score: score,
      positive_note: parsed.positive_note ?? "",
      messages: data.messages,
      source_title: data.source_title ?? null,
    };

    const { data: row, error } = await context.supabase
      .from("generations")
      .insert({
        user_id: context.userId,
        assignment_type: "tutor",
        title,
        prompt: data.source_title ?? null,
        source_text: data.source_text ?? null,
        output,
      })
      .select("id")
      .single();

    if (error) throw new Error(error.message);
    return { id: row.id as string, brain_work_score: score, positive_note: output.positive_note };
  });

const ExplainInput = z.object({
  prompt: z.string().trim().min(1).max(4000),
});

export const explainPrompt = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => ExplainInput.parse(input))
  .handler(async ({ data }) => {
    const { generateText } = await import("ai");
    const { createLovableAiGatewayProvider } = await import("./ai-gateway.server");
    const key = process.env.LOVABLE_API_KEY;
    if (!key) throw new Error("Missing LOVABLE_API_KEY");
    const gateway = createLovableAiGatewayProvider(key);
    const model = gateway("google/gemini-2.5-flash");

    const { text } = await generateText({
      model,
      system: `Rewrite a teacher's essay prompt in plain, friendly language a middle/high school student can easily understand. Keep it short (3-5 sentences). Explain (1) what the essay is actually asking, (2) what the student needs to argue or show, and (3) what to focus on. Do NOT write the essay or thesis for them.`,
      prompt: data.prompt,
    });
    const trimmed = text.trim();
    if (!trimmed) throw new Error("Couldn't explain the prompt.");
    return { explanation: trimmed };
  });

const OutlineInput = z.object({
  essay_type: z.string().trim().min(1).max(80),
  prompt: z.string().trim().min(1).max(4000),
  requirements: z.string().trim().max(1000).optional(),
  body_paragraphs: z.number().int().min(1).max(15),
  total_words: z.number().int().min(50).max(20000).optional(),
  source_title: z.string().max(300).optional(),
  source_text: z.string().max(40000).optional(),
});

export const buildOutline = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => OutlineInput.parse(input))
  .handler(async ({ data, context }) => {
    await assertGenerationAccess(context.supabase, context.userId);
    const { generateText } = await import("ai");
    const { createLovableAiGatewayProvider } = await import("./ai-gateway.server");
    const key = process.env.LOVABLE_API_KEY;
    if (!key) throw new Error("Missing LOVABLE_API_KEY");
    const gateway = createLovableAiGatewayProvider(key);
    const model = gateway("google/gemini-2.5-flash");

    const intro = data.total_words ? Math.round(data.total_words * 0.15) : null;
    const conclusion = data.total_words ? Math.round(data.total_words * 0.15) : null;
    const perBody =
      data.total_words && data.body_paragraphs > 0
        ? Math.round((data.total_words - (intro ?? 0) - (conclusion ?? 0)) / data.body_paragraphs)
        : null;

    const sourceContext =
      data.source_title || data.source_text
        ? `\n\nSOURCE:\n${data.source_title ? `Title: ${data.source_title}\n` : ""}${
            data.source_text ? `Excerpt:\n"""\n${data.source_text.slice(0, 6000)}\n"""` : ""
          }`
        : "";

    const wordTargets = data.total_words
      ? `\n\nTarget word counts: intro ~${intro}, each body ~${perBody}, conclusion ~${conclusion}.`
      : "";

    const { fetchPriorVariants, buildUniquenessClause, logVariant } = await import("./variation");
    const variantKey = `${(data.source_title || data.essay_type || "").trim()}::${data.body_paragraphs}`;
    const priors = await fetchPriorVariants(
      context.supabase,
      context.userId,
      "tutor_outline",
      variantKey,
    );
    const { clause: uniqueness } = buildUniquenessClause(
      priors,
      "ask different guiding questions, pick different angles, and avoid reusing any phrasings from the prior outlines",
    );

    const { text } = await generateText({
      model,
      system: `You build essay OUTLINES (not full essays) for a student. Return STRICT JSON only, no markdown fences. Use this exact shape:
{
  "thesis_hint": "<one sentence describing what their thesis should DO (not the thesis itself)>",
  "introduction": { "target_words": <int>, "points": ["<bullet>", "<bullet>"] },
  "body_paragraphs": [ { "topic": "<short topic sentence direction>", "target_words": <int>, "points": ["<bullet>", "<bullet>", "<bullet>"] } ],
  "conclusion": { "target_words": <int>, "points": ["<bullet>", "<bullet>"] }
}
Rules:
- Outline only — DO NOT write the thesis or full sentences for the student.
- Each item in "points" MUST be a short, friendly QUESTION written in plain everyday language a middle-school student can understand (no jargon, no academic vocabulary). The question should make the student think and decide what to write themselves.
- Use simple words. Keep each question under ~15 words. End every item with a question mark.
- Good examples: "What is the main thing you want your reader to believe?" / "What happened first in the story?" / "Why does this example matter to you?"
- Bad examples (do NOT do this): "Introduce the thesis." / "Provide textual evidence." / "Establish context."
- Match the requested number of body paragraphs exactly.
- Tailor the questions to the essay type and the teacher's prompt.` + uniqueness,
      temperature: 0.9,
      prompt: `Essay type: ${data.essay_type}
Teacher's prompt: ${data.prompt}
Requirements: ${data.requirements || "(not specified)"}
Body paragraphs needed: ${data.body_paragraphs}${wordTargets}${sourceContext}`,
    });

    let parsed: {
      thesis_hint: string;
      introduction: { target_words: number; points: string[] };
      body_paragraphs: { topic: string; target_words: number; points: string[] }[];
      conclusion: { target_words: number; points: string[] };
    };
    try {
      const clean = text
        .trim()
        .replace(/^```(?:json)?/i, "")
        .replace(/```$/, "")
        .trim();
      parsed = JSON.parse(clean);
    } catch {
      throw new Error("Couldn't build outline. Try again.");
    }
    await logVariant(
      context.supabase,
      context.userId,
      "tutor_outline",
      variantKey,
      JSON.stringify({
        thesis_hint: parsed.thesis_hint,
        intro: parsed.introduction?.points,
        bodies: parsed.body_paragraphs?.map((b) => ({ topic: b.topic, points: b.points })),
        conclusion: parsed.conclusion?.points,
      }),
    );
    return { outline: parsed };
  });

const ThesisInput = z.object({
  thesis: z.string().trim().min(1).max(2000),
  essay_type: z.string().trim().max(80).optional(),
  prompt: z.string().trim().max(4000).optional(),
  source_title: z.string().max(300).optional(),
  source_text: z.string().max(40000).optional(),
  attempt: z.number().int().min(1).max(50).optional(),
  previous_example_thesis: z.string().trim().max(2000).optional(),
});

export const checkThesis = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => ThesisInput.parse(input))
  .handler(async ({ data, context }) => {
    await assertGenerationAccess(context.supabase, context.userId);
    const { generateText } = await import("ai");
    const { createLovableAiGatewayProvider } = await import("./ai-gateway.server");
    const key = process.env.LOVABLE_API_KEY;
    if (!key) throw new Error("Missing LOVABLE_API_KEY");
    const gateway = createLovableAiGatewayProvider(key);
    const model = gateway("google/gemini-2.5-flash");

    const ctx =
      (data.essay_type ? `Essay type: ${data.essay_type}\n` : "") +
      (data.prompt ? `Teacher's prompt: ${data.prompt}\n` : "") +
      (data.source_title ? `Source title: ${data.source_title}\n` : "") +
      (data.source_text ? `Source excerpt:\n"""\n${data.source_text.slice(0, 4000)}\n"""\n` : "");

    const attempt = data.attempt ?? 1;
    const offerExample = attempt >= 2;

    const exampleRule = offerExample
      ? `

SPECIAL RULE FOR THIS REPLY: The student has already revised their thesis at least once and may be getting frustrated. In addition to your normal feedback, write ONE complete EXAMPLE thesis they could use as a model — based on their topic and prompt. Put it in the "example_thesis" field. Use plain, simple, everyday words a middle-school student would use — no fancy academic vocabulary. Make it clearly an example (not a command to copy), and keep your suggestions encouraging. If the rating is "great", you may leave "example_thesis" as an empty string.`
      : `

If the student is on their first attempt, leave "example_thesis" as an empty string.`;

    const { text } = await generateText({
      model,
      system: `You are a caring, encouraging middle/high school writing teacher. A student is showing you their THESIS STATEMENT and wants gentle feedback.

Your job:
1) Always start with sincere PRAISE — name at least one specific thing they did well (even a small thing). Be warm and human, never sarcastic.
2) Decide if the thesis is "great", "almost", or "needs work" — based on whether it: takes a clear position/claim, is specific (not vague), answers the prompt, and is arguable (not just a fact).
3) Give 1–3 short, friendly suggestions for how to make it even stronger. Phrase suggestions as questions or gentle nudges (e.g. "Could you name WHO and WHAT more clearly?"). If the student has already captured the gist of your suggestions from a prior round, say so — they do not need to match your suggestions word-for word. What matters is that their own idea comes through clearly. NEVER suggest tiny word-swaps or single-word changes (e.g. "most" vs "best", "many" vs "several", swapping one adjective for another). Suggestions must be about substantive things: clarity of the claim, specificity, position, evidence, or structure. If the only thing you could nitpick is a single word, give zero suggestions and mark it "great".
4) End with one encouraging sentence.${exampleRule}${goodEnoughRule(attempt, "thesis")}

Return STRICT JSON only, no markdown fences:
{
  "rating": "great" | "almost" | "needs_work",
  "praise": "<1-2 warm sentences naming what they did well>",
  "suggestions": ["<short friendly suggestion/question>", "..."],
  "encouragement": "<one short encouraging sentence>",
  "example_thesis": "<one complete example thesis sentence, or empty string>"
}`,
      prompt: `${ctx}\nStudent's attempt number: ${attempt}\nStudent's thesis:\n"""\n${data.thesis}\n"""`,
    });

    let parsed: {
      rating: "great" | "almost" | "needs_work";
      praise: string;
      suggestions: string[];
      encouragement: string;
      example_thesis?: string;
    };
    try {
      const clean = text
        .trim()
        .replace(/^```(?:json)?/i, "")
        .replace(/```$/, "")
        .trim();
      parsed = JSON.parse(clean);
    } catch {
      parsed = {
        rating: "almost",
        praise: "You took a real swing at putting your idea into words — that's the hardest part!",
        suggestions: ["Could you make your position a little more specific?"],
        encouragement: "You're closer than you think. Keep going!",
        example_thesis: "",
      };
    }
    const shouldAcceptThesis =
      countWordsForFeedback(data.thesis) >= 7 &&
      (attempt >= 4 || isVeryCloseToExample(data.thesis, data.previous_example_thesis));
    const matchesPreviousExample = isVeryCloseToExample(data.thesis, data.previous_example_thesis);
    parsed = normalizeGoodEnoughFeedback(
      parsed,
      attempt,
      shouldAcceptThesis,
      matchesPreviousExample,
    );
    if (shouldAcceptThesis && isVeryCloseToExample(data.thesis, data.previous_example_thesis)) {
      parsed.praise =
        "You did exactly what the example was meant to help you do: you turned it into a clear thesis in your own work.";
      parsed.encouragement = "This is strong enough to move on and keep building the essay.";
    }
    if (!offerExample || parsed.rating === "great") parsed.example_thesis = "";
    return { feedback: parsed };
  });

const ThesisSuggestInput = z.object({
  essay_type: z.string().trim().max(80).optional(),
  prompt: z.string().trim().max(4000).optional(),
  source_title: z.string().max(300).optional(),
  source_text: z.string().max(40000).optional(),
});

export const suggestThesisQuestions = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => ThesisSuggestInput.parse(input))
  .handler(async ({ data, context }) => {
    await assertGenerationAccess(context.supabase, context.userId);
    const { generateText } = await import("ai");
    const { createLovableAiGatewayProvider } = await import("./ai-gateway.server");
    const key = process.env.LOVABLE_API_KEY;
    if (!key) throw new Error("Missing LOVABLE_API_KEY");
    const gateway = createLovableAiGatewayProvider(key);
    const model = gateway("google/gemini-2.5-flash");

    const ctx =
      (data.essay_type ? `Essay type: ${data.essay_type}\n` : "") +
      (data.prompt ? `Teacher's prompt: ${data.prompt}\n` : "") +
      (data.source_title ? `Source title: ${data.source_title}\n` : "") +
      (data.source_text ? `Source excerpt:\n"""\n${data.source_text.slice(0, 4000)}\n"""\n` : "");

    const { fetchPriorVariants, buildUniquenessClause, logVariant } = await import("./variation");
    const variantKey = (data.source_title || data.essay_type || "general").trim();
    const priors = await fetchPriorVariants(
      context.supabase,
      context.userId,
      "tutor_thesis_q",
      variantKey,
    );
    const { clause: uniqueness } = buildUniquenessClause(
      priors,
      "ask 3 different guiding questions that probe different angles, vocabulary, and entry points",
    );

    const { text } = await generateText({
      model,
      system: `You are a caring middle/high school writing teacher helping a student START their thesis. You DO NOT write the thesis or any sentence the student could copy. Instead, give 3 short, friendly QUESTIONS that help them figure out what to say.

Rules:
- Every item MUST be a question ending in a question mark.
- Use plain, simple language a middle-school student understands. No jargon.
- Keep each question under ~15 words.
- Questions should help them find their position, the WHO/WHAT, and WHY it matters.
- Never include any draft thesis, partial sentence, or sentence stem like "I believe that..." that the student could just copy.

Return STRICT JSON only, no markdown fences:
{ "questions": ["<question 1>", "<question 2>", "<question 3>"] }` + uniqueness,
      prompt: ctx || "No extra context provided. Give universal thesis-starting questions.",
      temperature: 0.9,
    });

    let parsed: { questions: string[] };
    try {
      const clean = text
        .trim()
        .replace(/^```(?:json)?/i, "")
        .replace(/```$/, "")
        .trim();
      parsed = JSON.parse(clean);
    } catch {
      parsed = {
        questions: [
          "What is the main idea you want your reader to believe?",
          "Who or what is your essay really about?",
          "Why does this topic matter to you?",
        ],
      };
    }
    const finalQuestions = (parsed.questions ?? [])
      .filter((q) => typeof q === "string" && q.trim())
      .slice(0, 3);
    await logVariant(
      context.supabase,
      context.userId,
      "tutor_thesis_q",
      variantKey,
      JSON.stringify(finalQuestions),
    );
    return { questions: finalQuestions };
  });

// ---------- Paragraph-level assists (intro / body / conclusion) ----------

const SectionKind = z.enum(["introduction", "body", "conclusion"]);

const ParagraphSuggestInput = z.object({
  section_kind: SectionKind,
  section_label: z.string().trim().max(120).optional(),
  section_topic: z.string().trim().max(500).optional(),
  section_points: z.array(z.string().max(500)).max(20).optional(),
  thesis: z.string().trim().max(4000).optional(),
  essay_type: z.string().trim().max(80).optional(),
  prompt: z.string().trim().max(4000).optional(),
  source_title: z.string().max(300).optional(),
  source_text: z.string().max(40000).optional(),
});

export const suggestParagraphQuestions = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => ParagraphSuggestInput.parse(input))
  .handler(async ({ data, context }) => {
    await assertGenerationAccess(context.supabase, context.userId);
    const { generateText } = await import("ai");
    const { createLovableAiGatewayProvider } = await import("./ai-gateway.server");
    const key = process.env.LOVABLE_API_KEY;
    if (!key) throw new Error("Missing LOVABLE_API_KEY");
    const gateway = createLovableAiGatewayProvider(key);
    const model = gateway("google/gemini-2.5-flash");

    const ctx =
      (data.essay_type ? `Essay type: ${data.essay_type}\n` : "") +
      (data.prompt ? `Teacher's prompt: ${data.prompt}\n` : "") +
      (data.thesis ? `Student's thesis: ${data.thesis}\n` : "") +
      (data.section_label
        ? `Section: ${data.section_label}\n`
        : `Section kind: ${data.section_kind}\n`) +
      (data.section_topic ? `Topic direction for this paragraph: ${data.section_topic}\n` : "") +
      (data.section_points && data.section_points.length
        ? `Outline questions already shown for this paragraph:\n- ${data.section_points.join("\n- ")}\n`
        : "") +
      (data.source_title ? `Source title: ${data.source_title}\n` : "") +
      (data.source_text ? `Source excerpt:\n"""\n${data.source_text.slice(0, 4000)}\n"""\n` : "");

    const focusByKind: Record<string, string> = {
      introduction:
        "hooking the reader, giving just enough background, and pointing toward their thesis",
      body: "their topic sentence, the evidence/example they'll use, and the reasoning that connects evidence back to the thesis",
      conclusion:
        "restating their main point in fresh words, pulling the body paragraphs together, and ending with a 'so what' that matters",
    };

    const { fetchPriorVariants, buildUniquenessClause, logVariant } = await import("./variation");
    const baseKey = (data.source_title || data.thesis || data.essay_type || "general").trim();
    const variantKey = `${baseKey}::${data.section_kind}::${(data.section_label || data.section_topic || "").slice(0, 80)}`;
    const priors = await fetchPriorVariants(
      context.supabase,
      context.userId,
      "tutor_paragraph_q",
      variantKey,
    );
    const { clause: uniqueness } = buildUniquenessClause(
      priors,
      "ask 3 different guiding questions for this paragraph — different angles, vocabulary, and entry points than any prior set",
    );

    const { text } = await generateText({
      model,
      system: `You are a caring middle/high school writing teacher helping a student START a specific paragraph of their essay. You DO NOT write the paragraph, topic sentence, or any sentence the student could copy. Instead, give exactly 3 short, friendly QUESTIONS that help them figure out what to say.

For this ${data.section_kind} paragraph, focus your questions on: ${focusByKind[data.section_kind]}.

Rules:
- Every item MUST be a question ending in a question mark.
- Use plain, simple language a middle-school student understands. No jargon.
- Keep each question under ~15 words.
- Tailor the questions to the student's thesis and this paragraph's topic when given.
- Do NOT repeat the outline questions already shown — ask different, sharper ones.
- Never include a draft sentence or sentence stem they could just copy.

Return STRICT JSON only, no markdown fences:
{ "questions": ["<question 1>", "<question 2>", "<question 3>"] }` + uniqueness,
      prompt: ctx || "No extra context provided. Give universal questions for this paragraph.",
      temperature: 0.9,
    });

    let parsed: { questions: string[] };
    try {
      const clean = text
        .trim()
        .replace(/^```(?:json)?/i, "")
        .replace(/```$/, "")
        .trim();
      parsed = JSON.parse(clean);
    } catch {
      parsed = {
        questions: [
          "What is the main point you want this paragraph to make?",
          "What example or evidence best shows that point?",
          "How does this connect back to your thesis?",
        ],
      };
    }
    const finalQuestions = (parsed.questions ?? [])
      .filter((q) => typeof q === "string" && q.trim())
      .slice(0, 3);
    await logVariant(
      context.supabase,
      context.userId,
      "tutor_paragraph_q",
      variantKey,
      JSON.stringify(finalQuestions),
    );
    return { questions: finalQuestions };
  });

const ParagraphCheckInput = z.object({
  section_kind: SectionKind,
  section_label: z.string().trim().max(120).optional(),
  section_topic: z.string().trim().max(500).optional(),
  draft: z.string().trim().min(1).max(20000),
  thesis: z.string().trim().max(4000).optional(),
  essay_type: z.string().trim().max(80).optional(),
  prompt: z.string().trim().max(4000).optional(),
  source_title: z.string().max(300).optional(),
  source_text: z.string().max(40000).optional(),
  attempt: z.number().int().min(1).max(50).optional(),
});

export const checkParagraph = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => ParagraphCheckInput.parse(input))
  .handler(async ({ data, context }) => {
    await assertGenerationAccess(context.supabase, context.userId);
    const { generateText } = await import("ai");
    const { createLovableAiGatewayProvider } = await import("./ai-gateway.server");
    const key = process.env.LOVABLE_API_KEY;
    if (!key) throw new Error("Missing LOVABLE_API_KEY");
    const gateway = createLovableAiGatewayProvider(key);
    const model = gateway("google/gemini-2.5-flash");

    const ctx =
      (data.essay_type ? `Essay type: ${data.essay_type}\n` : "") +
      (data.prompt ? `Teacher's prompt: ${data.prompt}\n` : "") +
      (data.thesis ? `Student's thesis: ${data.thesis}\n` : "") +
      (data.section_label
        ? `Section: ${data.section_label}\n`
        : `Section kind: ${data.section_kind}\n`) +
      (data.section_topic ? `Topic direction for this paragraph: ${data.section_topic}\n` : "") +
      (data.source_title ? `Source title: ${data.source_title}\n` : "") +
      (data.source_text ? `Source excerpt:\n"""\n${data.source_text.slice(0, 4000)}\n"""\n` : "");

    const checklistByKind: Record<string, string> = {
      introduction:
        "opens with a hook, gives just enough context, and points clearly toward the thesis",
      body: "starts with a clear topic sentence, includes specific evidence or example, and explains how that evidence supports the thesis",
      conclusion:
        "restates the main point in new words (not copy-paste), ties the body paragraphs together, and ends with a meaningful 'so what'",
    };

    const { text } = await generateText({
      model,
      system: `You are a caring, encouraging middle/high school writing teacher. A student is showing you a DRAFT of their ${data.section_kind} paragraph and wants gentle feedback.

Your job:
1) Always start with sincere PRAISE — name at least one specific thing they did well in THIS paragraph. Be warm and human.
2) Decide if the paragraph is "great", "almost", or "needs_work" — based on whether it ${checklistByKind[data.section_kind]}, stays on topic, and uses clear language.
3) Give 1–3 short, friendly suggestions for how to make it stronger. Phrase suggestions as questions or gentle nudges. If the student already addressed a prior suggestion in spirit (even with different wording), say so — they do not have to match word-for-word. Do not introduce brand-new suggestions on later attempts unless a core requirement is missing. NEVER suggest tiny word-swaps or single-word changes (e.g. "most" vs "best", "many" vs "several", swapping one adjective for another). Suggestions must be about substantive things: clarity, evidence, reasoning, structure, or staying on topic. If the only thing you could nitpick is a single word, give zero suggestions and mark it "great".
4) End with one encouraging sentence.

CRITICAL RULES:
- NEVER write a sample paragraph, topic sentence, or any sentence the student could copy. You are a tutor, not a ghostwriter.
- Use plain, simple, everyday words a middle-school student would use — no fancy academic vocabulary.
${goodEnoughRule(data.attempt ?? 1, "paragraph")}

Return STRICT JSON only, no markdown fences:
{
  "rating": "great" | "almost" | "needs_work",
  "praise": "<1-2 warm sentences naming what they did well>",
  "suggestions": ["<short friendly suggestion/question>", "..."],
  "encouragement": "<one short encouraging sentence>"
}`,
      prompt: `${ctx}\nStudent's attempt number: ${data.attempt ?? 1}\nStudent's draft:\n"""\n${data.draft}\n"""`,
    });

    let parsed: {
      rating: "great" | "almost" | "needs_work";
      praise: string;
      suggestions: string[];
      encouragement: string;
    };
    try {
      const clean = text
        .trim()
        .replace(/^```(?:json)?/i, "")
        .replace(/```$/, "")
        .trim();
      parsed = JSON.parse(clean);
    } catch {
      parsed = {
        rating: "almost",
        praise: "You got real ideas down on the page — that's the hardest part!",
        suggestions: ["Could you make your main point a little clearer up front?"],
        encouragement: "You're closer than you think. Keep going!",
      };
    }
    return { feedback: normalizeGoodEnoughFeedback(parsed, data.attempt ?? 1) };
  });

// ---------- Check a single CLAIM (topic sentence) ----------

const ClaimCheckInput = z.object({
  claim: z.string().trim().min(1).max(2000),
  section_label: z.string().trim().max(120).optional(),
  section_topic: z.string().trim().max(500).optional(),
  thesis: z.string().trim().max(4000).optional(),
  essay_type: z.string().trim().max(80).optional(),
  prompt: z.string().trim().max(4000).optional(),
  attempt: z.number().int().min(1).max(50).optional(),
});

export const checkClaim = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => ClaimCheckInput.parse(input))
  .handler(async ({ data, context }) => {
    await assertGenerationAccess(context.supabase, context.userId);
    const { generateText } = await import("ai");
    const { createLovableAiGatewayProvider } = await import("./ai-gateway.server");
    const key = process.env.LOVABLE_API_KEY;
    if (!key) throw new Error("Missing LOVABLE_API_KEY");
    const gateway = createLovableAiGatewayProvider(key);
    const model = gateway("google/gemini-2.5-flash");

    const ctx =
      (data.essay_type ? `Essay type: ${data.essay_type}\n` : "") +
      (data.prompt ? `Teacher's prompt: ${data.prompt}\n` : "") +
      (data.thesis ? `Student's thesis: ${data.thesis}\n` : "") +
      (data.section_label ? `Section: ${data.section_label}\n` : "") +
      (data.section_topic ? `Topic direction for this paragraph: ${data.section_topic}\n` : "");

    const attempt = data.attempt ?? 1;

    const { text } = await generateText({
      model,
      system: `You are a caring, encouraging middle/high school writing teacher. A student is showing you just the CLAIM (topic sentence) for a body paragraph — not the whole paragraph. They have NOT added evidence or reasoning yet.

Your job:
1) Start with sincere PRAISE for what they did well in the claim itself.
2) Decide if the claim is "great", "almost", or "needs_work" — based ONLY on the claim: does it take a clear, arguable position, is it specific (not vague), and does it clearly support the thesis (if a thesis was given)?
3) Give 0–2 short, friendly suggestions about the CLAIM ONLY. Do NOT tell them to add evidence, add a quote, add reasoning, or explain why — those are the next steps and they know that. Focus only on the claim sentence itself: clarity, specificity, position, connection to thesis.
4) NEVER suggest tiny word-swaps or single-word changes. If the only thing you could nitpick is a single word, give zero suggestions and mark it "great".
5) End with one short encouraging sentence.

CRITICAL RULES:
- NEVER write a sample claim or any sentence the student could copy.
- Use plain, everyday words a middle-school student would use.
${goodEnoughRule(attempt, "paragraph")}

Return STRICT JSON only, no markdown fences:
{
  "rating": "great" | "almost" | "needs_work",
  "praise": "<1-2 warm sentences>",
  "suggestions": ["<short friendly suggestion about the claim>", "..."],
  "encouragement": "<one short encouraging sentence>"
}`,
      prompt: `${ctx}\nStudent's attempt number: ${attempt}\nStudent's claim:\n"""\n${data.claim}\n"""`,
    });

    let parsed: {
      rating: "great" | "almost" | "needs_work";
      praise: string;
      suggestions: string[];
      encouragement: string;
    };
    try {
      const clean = text
        .trim()
        .replace(/^```(?:json)?/i, "")
        .replace(/```$/, "")
        .trim();
      parsed = JSON.parse(clean);
    } catch {
      parsed = {
        rating: "almost",
        praise: "You put a real position into words — that's the hardest part!",
        suggestions: [],
        encouragement: "Looks good — keep going to evidence.",
      };
    }
    return { feedback: normalizeGoodEnoughFeedback(parsed, attempt) };
  });

// ---------- Find quotes from the source for a specific paragraph ----------

const FindQuotesInput = z.object({
  section_kind: SectionKind,
  section_label: z.string().trim().max(120).optional(),
  section_topic: z.string().trim().max(500).optional(),
  count: z.number().int().min(1).max(5),
  thesis: z.string().trim().max(4000).optional(),
  draft: z.string().trim().max(20000).optional(),
  essay_type: z.string().trim().max(80).optional(),
  prompt: z.string().trim().max(4000).optional(),
  source_title: z.string().max(300).optional(),
  source_text: z.string().max(40000).optional(),
});

export const findQuotes = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => FindQuotesInput.parse(input))
  .handler(async ({ data, context }) => {
    await assertGenerationAccess(context.supabase, context.userId);
    const hasSourceText = !!(data.source_text && data.source_text.trim().length >= 20);
    const hasSourceTitle = !!(data.source_title && data.source_title.trim().length >= 2);
    if (!hasSourceText && !hasSourceTitle) {
      throw new Error("Add a source title or paste your source text so I can find quotes.");
    }
    const { generateText } = await import("ai");
    const { createLovableAiGatewayProvider } = await import("./ai-gateway.server");
    const key = process.env.LOVABLE_API_KEY;
    if (!key) throw new Error("Missing LOVABLE_API_KEY");
    const gateway = createLovableAiGatewayProvider(key);
    const model = gateway("google/gemini-2.5-flash");

    const ctx =
      (data.essay_type ? `Essay type: ${data.essay_type}\n` : "") +
      (data.prompt ? `Teacher's prompt: ${data.prompt}\n` : "") +
      (data.thesis ? `Student's thesis: ${data.thesis}\n` : "") +
      (data.section_label
        ? `Section: ${data.section_label}\n`
        : `Section kind: ${data.section_kind}\n`) +
      (data.section_topic ? `Topic for this paragraph: ${data.section_topic}\n` : "") +
      (data.draft ? `Student's current draft for this paragraph:\n"""\n${data.draft}\n"""\n` : "") +
      (data.source_title ? `Source title: ${data.source_title}\n` : "") +
      (hasSourceText
        ? `Source text:\n"""\n${data.source_text!.slice(0, 12000)}\n"""\n`
        : `Source text: (NOT PROVIDED — pull from your knowledge of the source titled above)\n`);

    const systemRulesWithText = `You help a student find REAL direct quotes from their source to support a specific paragraph of their essay. You DO NOT write the paragraph for them — you only surface evidence they can choose from.

CRITICAL RULES:
- Every quote MUST be copied WORD-FOR-WORD from the provided source text. Do not paraphrase, summarize, or invent. If you cannot find ${data.count} suitable quotes, return fewer.
- Keep each quote between 4 and 40 words. Trim long passages to the most relevant sentence or clause.
- Pick quotes that directly support this paragraph's topic and the student's thesis.
- For each quote, give an MLA-style parenthetical citation guess based on the source title and any author name in the title (e.g. "(Orwell 12)" or '("Source Title")'). If no page number is known, use the author's last name or shortened title only.
- Write a short "why_it_works" note (1 sentence, plain words) telling the student how they could use this quote — NOT a sentence they can copy.`;

    const systemRulesNoText = `You help a student find direct quotes from their source to support a specific paragraph of their essay. You DO NOT write the paragraph for them — you only surface evidence they can choose from.

The student did NOT paste the source text. Use your own knowledge of the source from the title (and author if implied) to recall well-known direct quotes from it.

CRITICAL RULES:
- Only return quotes you are HIGHLY CONFIDENT are accurate from the actual source. If you are not sure the source exists or you don't have reliable knowledge of it, return an empty quotes array. NEVER fabricate quotes.
- Quotes must read word-for-word as they appear in the source. Do not paraphrase or summarize.
- Keep each quote between 4 and 40 words.
- Pick quotes that directly support this paragraph's topic and the student's thesis.
- For each quote, give an MLA-style parenthetical citation (author last name + page if known, otherwise author last name or shortened title only).
- Write a short "why_it_works" note (1 sentence, plain words) telling the student how they could use this quote — NOT a sentence they can copy.
- In the "why_it_works", briefly add: "Double-check this quote against your copy of the source."`;

    const { text } = await generateText({
      model,
      system: `${hasSourceText ? systemRulesWithText : systemRulesNoText}

Return STRICT JSON only, no markdown fences:
{
  "quotes": [
    { "quote": "<word-for-word quote>", "citation": "<MLA parenthetical>", "why_it_works": "<one short sentence>" }
  ]
}`,
      prompt: `${ctx}\nFind up to ${data.count} quote(s) the student could use.`,
    });

    let parsed: { quotes: { quote: string; citation: string; why_it_works: string }[] };
    try {
      const clean = text
        .trim()
        .replace(/^```(?:json)?/i, "")
        .replace(/```$/, "")
        .trim();
      parsed = JSON.parse(clean);
    } catch {
      throw new Error("Couldn't pull quotes from the source. Try again.");
    }
    const quotes = (parsed.quotes ?? [])
      .filter((q) => q && typeof q.quote === "string" && q.quote.trim())
      .slice(0, data.count)
      .map((q) => ({
        quote: q.quote.trim().replace(/^["“”]+|["“”]+$/g, ""),
        citation: (q.citation || "").trim(),
        why_it_works: (q.why_it_works || "").trim(),
      }));
    return { quotes };
  });

// ---------- Grade the whole brainwork ----------

const GradeInput = z.object({
  essay_type: z.string().trim().max(80).optional(),
  prompt: z.string().trim().max(4000).optional(),
  thesis: z.string().trim().max(4000).optional(),
  introduction: z.string().trim().max(20000).optional(),
  body_paragraphs: z
    .array(
      z.object({
        topic: z.string().max(500).optional(),
        text: z.string().max(20000),
      }),
    )
    .max(15)
    .optional(),
  conclusion: z.string().trim().max(20000).optional(),
  source_title: z.string().max(300).optional(),
});

export const gradeBrainwork = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => GradeInput.parse(input))
  .handler(async ({ data, context }) => {
    await assertGenerationAccess(context.supabase, context.userId);
    const { generateText } = await import("ai");
    const { createLovableAiGatewayProvider } = await import("./ai-gateway.server");
    const key = process.env.LOVABLE_API_KEY;
    if (!key) throw new Error("Missing LOVABLE_API_KEY");
    const gateway = createLovableAiGatewayProvider(key);
    const model = gateway("google/gemini-2.5-flash");

    const bodyBlock = (data.body_paragraphs ?? [])
      .map((b, i) => `BODY ${i + 1}${b.topic ? ` (${b.topic})` : ""}:\n"""\n${b.text}\n"""`)
      .join("\n\n");

    const essayBlock =
      (data.essay_type ? `Essay type: ${data.essay_type}\n` : "") +
      (data.prompt ? `Teacher's prompt: ${data.prompt}\n` : "") +
      (data.source_title ? `Source: ${data.source_title}\n` : "") +
      (data.thesis ? `\nTHESIS:\n"""\n${data.thesis}\n"""\n` : "") +
      (data.introduction ? `\nINTRODUCTION:\n"""\n${data.introduction}\n"""\n` : "") +
      (bodyBlock ? `\n${bodyBlock}\n` : "") +
      (data.conclusion ? `\nCONCLUSION:\n"""\n${data.conclusion}\n"""\n` : "");

    const { text } = await generateText({
      model,
      system: `You are a caring middle/high school writing teacher giving a student a final, encouraging grade on the brainwork they did in tutor mode. They wrote every word themselves — celebrate that.

Your job:
1) Give an overall letter grade (A, A-, B+, B, B-, C+, C, C-, or "Keep going") based on thesis clarity, each paragraph having a clear point + evidence/reasoning, flow from intro -> body -> conclusion, and staying on the teacher's prompt.
2) Write a warm 2-3 sentence overall summary celebrating what they pulled off.
3) Give 2-3 specific STRENGTHS (what's working).
4) Give 2-3 short, friendly GROWTH AREAS (what they could sharpen next time).
5) End with one encouraging sentence.

CRITICAL RULES:
- NEVER rewrite their sentences or hand them a sample paragraph. You are grading their brainwork, not ghostwriting.
- Use plain, everyday words — no academic jargon.
- Be honest but kind. If a section is missing, name it gently.

Return STRICT JSON only, no markdown fences:
{
  "grade": "<letter grade or 'Keep going'>",
  "summary": "<2-3 warm sentences>",
  "strengths": ["<short specific strength>", "..."],
  "growth_areas": ["<short friendly growth area>", "..."],
  "encouragement": "<one short encouraging sentence>"
}`,
      prompt: essayBlock || "Student hasn't drafted much yet — encourage them to keep going.",
    });

    let parsed: {
      grade: string;
      summary: string;
      strengths: string[];
      growth_areas: string[];
      encouragement: string;
    };
    try {
      const clean = text
        .trim()
        .replace(/^```(?:json)?/i, "")
        .replace(/```$/, "")
        .trim();
      parsed = JSON.parse(clean);
    } catch {
      parsed = {
        grade: "Keep going",
        summary: "You've put real thinking onto the page — that's the win.",
        strengths: ["You showed up and did the work in your own words."],
        growth_areas: ["Add a little more once you've finished every section."],
        encouragement: "Proud of you. Keep going!",
      };
    }
    return { report: parsed };
  });

// ---------- Save / resume tutor work-in-progress ----------

const TutorWorkSchema = z.object({
  essay_type: z.string().max(80),
  teacher_prompt: z.string().max(8000),
  body_paragraphs: z.number().int().min(1).max(15),
  total_words: z.number().int().min(0).max(20000).nullable(),
  source_title: z.string().max(300),
  source_text: z.string().max(40000),
  outline: z.unknown().nullable(),
  drafts: z.array(z.string().max(20000)).max(20),
  thesis: z.string().max(4000),
});

const SaveTutorWorkInput = z.object({
  id: z.string().uuid().optional(),
  work: TutorWorkSchema,
});

export const saveTutorWork = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => SaveTutorWorkInput.parse(input))
  .handler(async ({ data, context }) => {
    const work = data.work;
    const title =
      work.teacher_prompt.trim().slice(0, 80) ||
      work.source_title.trim().slice(0, 80) ||
      "Tutor session";

    const output = {
      kind: "tutor_work" as const,
      essay_type: work.essay_type,
      teacher_prompt: work.teacher_prompt,
      body_paragraphs: work.body_paragraphs,
      total_words: work.total_words,
      source_title: work.source_title,
      outline: (work.outline ?? null) as never,
      drafts: work.drafts,
      thesis: work.thesis,
    } as never;

    if (data.id) {
      const { data: row, error } = await context.supabase
        .from("generations")
        .update({
          title,
          prompt: work.teacher_prompt || null,
          source_text: work.source_text || null,
          output,
        })
        .eq("id", data.id)
        .eq("user_id", context.userId)
        .select("id")
        .single();
      if (error) throw new Error(error.message);
      return { id: row.id as string };
    }

    const { data: row, error } = await context.supabase
      .from("generations")
      .insert({
        user_id: context.userId,
        assignment_type: "tutor",
        title,
        prompt: work.teacher_prompt || null,
        source_text: work.source_text || null,
        output,
      })
      .select("id")
      .single();
    if (error) throw new Error(error.message);
    return { id: row.id as string };
  });

export const loadTutorWork = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => z.object({ id: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }) => {
    const { data: row, error } = await context.supabase
      .from("generations")
      .select("id, title, prompt, source_text, output")
      .eq("id", data.id)
      .single();
    if (error) throw new Error(error.message);
    return row;
  });

const AutoCorrectInput = z.object({
  text: z.string().min(1).max(10000),
});

export const autoCorrectText = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => AutoCorrectInput.parse(input))
  .handler(async ({ data, context }) => {
    await assertGenerationAccess(context.supabase, context.userId);

    const { generateText } = await import("ai");
    const { createLovableAiGatewayProvider } = await import("./ai-gateway.server");

    const key = process.env.LOVABLE_API_KEY;
    if (!key) throw new Error("Missing LOVABLE_API_KEY");

    const gateway = createLovableAiGatewayProvider(key);
    const model = gateway("google/gemini-2.5-flash-lite");

    const { text } = await generateText({
      model,
      system: `You are a strict spelling and grammar auto-corrector. Your ONLY job:
- Fix obvious spelling mistakes.
- Fix obvious grammar mistakes (subject-verb agreement, verb tense, articles, basic punctuation, capitalization at sentence starts).
- DO NOT reword, rephrase, restructure, improve style, add ideas, remove ideas, or change meaning.
- DO NOT change the student's vocabulary choices unless misspelled.
- DO NOT add or remove sentences.
- Preserve all line breaks and paragraph structure exactly.
- If the text is already correct, return it unchanged.
- Output ONLY the corrected text. No quotes, no commentary, no markdown.`,
      prompt: data.text,
    });

    // Strip stray wrapping quotes the model may add, preserve inner whitespace
    let corrected = text;
    if (/^["'].*["']$/s.test(corrected.trim())) {
      corrected = corrected.trim().slice(1, -1);
    }
    return { corrected };
  });
